import { useLayoutEffect, useRef, useState } from 'react';
import { Popover, Portal } from '@headlessui/react';
import ChevronDown from 'svg/ChevronDown';
import ChevronUp from 'svg/ChevronUp';
import Location from 'svg/Location';
import Search2 from 'svg/Search2';
import classNames from 'styles/utils/classNames';
import CheckboxMark from './CheckboxMark';
import type { Neighborhood } from './useNeighborhoods';

interface Props {
  options: Neighborhood[];
  value: Neighborhood[];
  onChange: (rows: Neighborhood[]) => void;
  placeholder?: string;
  className?: string;
  /** `field` = the grey input in forms and sheets; `pill` = the Home bar's white desktop pill. */
  variant?: 'field' | 'pill';
  /** Pill only: the trigger's chrome (height, border, shadow), so it matches its sibling dropdowns. */
  buttonClassName?: string;
  /** Pill only: added while open. */
  openButtonClassName?: string;
}

const ROW_CLASS = 'flex w-full shrink-0 items-center gap-ds-sm rounded-md px-ds-md text-left';
// Figma: 42px rows in the sheet/form dropdown, 40px in the Home bar's pill.
const ROW_HEIGHT = { field: 'h-[2.625rem]', pill: 'h-10' } as const;

function OptionRow({
  option,
  isSelected,
  onToggle,
  heightClass,
}: {
  option: Neighborhood;
  isSelected: boolean;
  onToggle: () => void;
  heightClass: string;
}) {
  return (
    <button type="button" onClick={onToggle} className={classNames(ROW_CLASS, heightClass)}>
      <CheckboxMark checked={isSelected} />
      <span
        className={classNames(
          'typography-product-body truncate',
          isSelected
            ? 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary'
            : 'text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder',
        )}
      >
        {option.name}
      </span>
    </button>
  );
}

const PANEL_GAP_PX = 8;
const PANEL_MAX_HEIGHT_PX = 26 * 16;

/** Fixed-position box under the trigger, re-measured while anything scrolls or resizes. */
function useAnchoredBox(anchor: React.RefObject<HTMLElement>) {
  const [box, setBox] = useState<{ top: number; left: number; width: number; maxHeight: number }>();

  useLayoutEffect(() => {
    const measure = () => {
      const rect = anchor.current?.getBoundingClientRect();
      if (!rect) return;
      const top = rect.bottom + PANEL_GAP_PX;
      const next = {
        top,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(PANEL_MAX_HEIGHT_PX, window.innerHeight - top - PANEL_GAP_PX),
      };
      // Scroll fires per frame; only re-render the list when the trigger really moved.
      setBox((previous) =>
        previous &&
        previous.top === next.top &&
        previous.left === next.left &&
        previous.width === next.width &&
        previous.maxHeight === next.maxHeight
          ? previous
          : next,
      );
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [anchor]);

  return box;
}

/** The open panel. Mounts fresh on every open, so the pinned set is that moment's selection. */
function Panel({
  options,
  value,
  onChange,
  anchor,
  variant,
}: Pick<Props, 'options' | 'value' | 'onChange'> & {
  anchor: React.RefObject<HTMLButtonElement>;
  variant: NonNullable<Props['variant']>;
}) {
  const rowHeight = ROW_HEIGHT[variant];
  const [query, setQuery] = useState('');
  // The sheet's body scrolls and clips, so the list is portaled and pinned under the trigger.
  const box = useAnchoredBox(anchor);
  // Figma: on reopen the already-selected rows sit above a divider, in list order;
  // toggling inside one open session must not reshuffle rows under the finger.
  const [pinnedIds] = useState(() => new Set(value.map((row) => row.id)));
  const selectedIds = new Set(value.map((row) => row.id));

  const matches = (option: Neighborhood) =>
    option.name.toLowerCase().includes(query.trim().toLowerCase());
  const pinned = options.filter((option) => pinnedIds.has(option.id) && matches(option));
  const rest = options.filter((option) => !pinnedIds.has(option.id) && matches(option));

  const toggle = (option: Neighborhood) =>
    onChange(
      selectedIds.has(option.id) ? value.filter((row) => row.id !== option.id) : [...value, option],
    );

  if (!box) return null;

  return (
    <Portal>
      <Popover.Panel
        static
        style={{ top: box.top, left: box.left, width: box.width, maxHeight: box.maxHeight }}
        className="fixed z-[60] flex flex-col rounded-[1.25rem] bg-color-bg-lightmode-primary p-ds-md shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)] dark:bg-color-bg-darkmode-primary"
      >
        <label className="flex h-10 shrink-0 items-center gap-ds-xs rounded-full bg-color-bg-input-lightmode-primary px-ds-lg dark:bg-color-bg-input-darkmode-primary">
          <Search2 className="h-5 w-5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search"
            autoFocus
            className="input-unstyled typography-product-body w-full border-none bg-transparent p-0 text-color-text-lightmode-primary outline-none ring-0 placeholder:text-color-text-lightmode-placeholder focus:outline-none focus:ring-0 dark:text-color-text-darkmode-primary dark:placeholder:text-color-text-darkmode-placeholder"
          />
        </label>

        <div className="mt-ds-md flex min-h-0 flex-col overflow-y-auto">
          {pinned.map((option) => (
            <OptionRow
              key={option.id}
              option={option}
              isSelected={selectedIds.has(option.id)}
              onToggle={() => toggle(option)}
              heightClass={rowHeight}
            />
          ))}
          {pinned.length > 0 && rest.length > 0 && (
            <div className="my-px h-px w-full shrink-0 bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
          )}
          {rest.map((option) => (
            <OptionRow
              key={option.id}
              option={option}
              isSelected={selectedIds.has(option.id)}
              onToggle={() => toggle(option)}
              heightClass={rowHeight}
            />
          ))}
          {pinned.length === 0 && rest.length === 0 && (
            <p className="typography-product-body px-ds-md py-ds-sm text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder">
              No neighborhoods match
            </p>
          )}
        </div>
      </Popover.Panel>
    </Portal>
  );
}

/**
 * Searchable multi-select of neighborhoods (Figma "Dropdown with active search"):
 * an input-styled trigger reading "East Village (+2)", a search pill and a
 * checkbox list with the current selection pinned to the top on reopen.
 */
export default function NeighborhoodSelect({
  options,
  value,
  onChange,
  placeholder = 'Select neighborhoods',
  className,
  variant = 'field',
  buttonClassName,
  openButtonClassName,
}: Props) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isPill = variant === 'pill';
  // Field: "East Village (+2)". Pill (Figma Home bar): "(3) East Village, Manhattan".
  const label =
    value.length === 0
      ? placeholder
      : isPill
      ? `(${value.length}) ${value[0].name}, ${value[0].city.name}`
      : value.length === 1
      ? value[0].name
      : `${value[0].name} (+${value.length - 1})`;

  return (
    <Popover className={classNames('relative', className)}>
      {({ open }) => (
        <>
          <Popover.Button
            ref={buttonRef}
            className={classNames(
              'flex w-full items-center justify-between gap-ds-xs text-left',
              isPill
                ? classNames(
                    'rounded-full bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary',
                    buttonClassName,
                    open && openButtonClassName,
                  )
                : 'h-[2.625rem] rounded-md bg-color-bg-input-lightmode-primary px-3.5 dark:bg-color-bg-input-darkmode-primary',
              !isPill &&
                open &&
                'ring-1 ring-color-border-hover-lightmode ring-offset-2 dark:ring-color-border-hover-darkmode',
            )}
          >
            <span className="flex min-w-0 flex-1 items-center gap-ds-xs">
              {isPill ? (
                <img
                  src="/images/app/pin-gradient.svg"
                  alt=""
                  className="h-6 w-6 shrink-0 object-contain"
                />
              ) : (
                <Location className="h-5 w-5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
              )}
              <span
                className={classNames(
                  'truncate',
                  isPill ? 'typography-product-subheading' : 'typography-product-body',
                  value.length === 0
                    ? 'text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder'
                    : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                )}
              >
                {label}
              </span>
            </span>
            {open ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
            )}
          </Popover.Button>
          {open && (
            <Panel
              options={options}
              value={value}
              onChange={onChange}
              anchor={buttonRef}
              variant={variant}
            />
          )}
        </>
      )}
    </Popover>
  );
}
